import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import hospitalConfig from '../config/hospitalConfig';
import { subscribeHospitalConfig } from '../services/hospitalConfigService';

const SessionContext = createContext({});

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};

const CHANNEL_NAME = 'kmch_communitycare_session';
const STORAGE_LOCK_KEY = 'kmch_session_locked';
const STORAGE_LOCKED_AT_KEY = 'kmch_session_locked_at';
const STORAGE_LAST_ACTIVE_KEY = 'kmch_last_activity';
const STORAGE_SYNC_KEY = 'kmch_session_sync_event';

export const SessionProvider = ({ children }) => {
  const { user, userProfile, loading: authLoading, signOut, verifyPassword, logSessionEvent } = useAuth();

  const [liveSecurityConfig, setLiveSecurityConfig] = useState(hospitalConfig?.sessionSecurity);

  useEffect(() => {
    const unsub = subscribeHospitalConfig((cfg) => {
      if (cfg?.sessionSecurity) {
        setLiveSecurityConfig(cfg.sessionSecurity);
      }
    });
    return () => unsub();
  }, []);

  // Configuration from hospitalConfig (reactive from Firestore with fallback)
  const config = liveSecurityConfig || {
    idleTimeoutMinutes: 60,
    warningDurationSeconds: 60,
    lockTimeoutMinutes: 60,
    autoSaveDraftsOnLock: true
  };

  const idleTimeoutMs = (config.idleTimeoutMinutes || 60) * 60 * 1000;
  const warningDurationMs = (config.warningDurationSeconds || 60) * 1000;
  const lockTimeoutMs = (config.lockTimeoutMinutes || 60) * 60 * 1000;

  // Initialize lock state from storage if session was already locked
  const [isLocked, setIsLocked] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_LOCK_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [showWarning, setShowWarning] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(config.warningDurationSeconds || 60);

  const lastActivityRef = useRef(Date.now());
  const lockedAtRef = useRef((() => {
    try {
      const stored = localStorage.getItem(STORAGE_LOCKED_AT_KEY);
      return stored ? parseInt(stored, 10) : null;
    } catch {
      return null;
    }
  })());
  const broadcastChannelRef = useRef(null);
  const throttleTimeoutRef = useRef(null);

  // Broadcast messages to other browser tabs
  const broadcast = useCallback((message) => {
    try {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage(message);
      }
      // Fallback via localStorage storage event for older browser tabs
      localStorage.setItem(STORAGE_SYNC_KEY, JSON.stringify({ ...message, _ts: Date.now() }));
    } catch (e) {
      // Ignore broadcast serialization errors
    }
  }, []);

  // Dispatch draft save event before locking or terminating
  const triggerDraftAutoSave = useCallback(() => {
    if (config.autoSaveDraftsOnLock && typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('communitycare:save_draft', {
          detail: {
            timestamp: Date.now(),
            userId: user?.uid
          }
        }));
      } catch (err) {
        console.warn('Draft auto-save event dispatch failed:', err);
      }
    }
  }, [config.autoSaveDraftsOnLock, user?.uid]);

  // Lock station handler
  const lockStation = useCallback((reason = 'manual') => {
    if (!user) return;

    triggerDraftAutoSave();

    const now = Date.now();
    setIsLocked(true);
    setShowWarning(false);
    lockedAtRef.current = now;

    try {
      localStorage.setItem(STORAGE_LOCK_KEY, 'true');
      localStorage.setItem(STORAGE_LOCKED_AT_KEY, now.toString());
    } catch (e) {
      console.warn('Storage write failed on lock:', e);
    }

    broadcast({ type: 'LOCK', reason, lockedAt: now });

    if (logSessionEvent) {
      logSessionEvent(reason === 'idle' ? 'SESSION_LOCKED_IDLE' : 'SESSION_LOCKED_MANUAL', {
        reason,
        locked_at: new Date(now).toISOString()
      });
    }
  }, [user, triggerDraftAutoSave, broadcast, logSessionEvent]);

  // Unlock station handler
  const unlockStation = useCallback(async (password) => {
    if (!user) return { success: false, error: 'No active session' };

    const result = await verifyPassword(password);
    if (result.success) {
      setIsLocked(false);
      setShowWarning(false);
      lockedAtRef.current = null;
      lastActivityRef.current = Date.now();

      try {
        localStorage.removeItem(STORAGE_LOCK_KEY);
        localStorage.removeItem(STORAGE_LOCKED_AT_KEY);
        localStorage.setItem(STORAGE_LAST_ACTIVE_KEY, Date.now().toString());
      } catch (e) {
        console.warn('Storage clear failed on unlock:', e);
      }

      broadcast({ type: 'UNLOCK', timestamp: Date.now() });

      if (logSessionEvent) {
        logSessionEvent('SESSION_UNLOCKED', {
          unlocked_at: new Date().toISOString()
        });
      }

      return { success: true };
    }

    return result;
  }, [user, verifyPassword, broadcast, logSessionEvent]);

  // Extend active session / reset idle timer
  const extendSession = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    setShowWarning(false);

    try {
      localStorage.setItem(STORAGE_LAST_ACTIVE_KEY, now.toString());
    } catch (e) {
      // Ignore
    }

    broadcast({ type: 'ACTIVITY', timestamp: now });
  }, [broadcast]);

  // Hard logout handler
  const handleHardLogout = useCallback(async (reason = 'idle_timeout') => {
    triggerDraftAutoSave();

    setIsLocked(false);
    setShowWarning(false);
    lockedAtRef.current = null;

    try {
      localStorage.removeItem(STORAGE_LOCK_KEY);
      localStorage.removeItem(STORAGE_LOCKED_AT_KEY);
      localStorage.removeItem(STORAGE_LAST_ACTIVE_KEY);
    } catch (e) {
      // Ignore
    }

    broadcast({ type: 'LOGOUT', reason });

    if (logSessionEvent) {
      logSessionEvent('SESSION_HARD_LOGOUT', {
        reason,
        terminated_at: new Date().toISOString()
      });
    }

    if (signOut) {
      await signOut();
    }

    // Force redirection to login with explanation parameter
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = `/login?reason=${encodeURIComponent(reason)}`;
    }
  }, [triggerDraftAutoSave, broadcast, logSessionEvent, signOut]);

  // Throttled activity updater
  const recordUserActivity = useCallback(() => {
    // If locked, background window clicks should NOT count as active work
    if (isLocked || !user) return;

    if (throttleTimeoutRef.current) return;

    throttleTimeoutRef.current = setTimeout(() => {
      throttleTimeoutRef.current = null;
    }, 10000); // Throttle activity to once every 10 seconds

    lastActivityRef.current = Date.now();
    try {
      localStorage.setItem(STORAGE_LAST_ACTIVE_KEY, Date.now().toString());
    } catch (e) {
      // Ignore
    }
    broadcast({ type: 'ACTIVITY', timestamp: Date.now() });
  }, [isLocked, user, broadcast]);

  // Setup BroadcastChannel and storage event listeners for multi-tab sync
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initialize BroadcastChannel
    try {
      if ('BroadcastChannel' in window) {
        broadcastChannelRef.current = new BroadcastChannel(CHANNEL_NAME);
        broadcastChannelRef.current.onmessage = (event) => {
          const data = event.data;
          if (!data || typeof data !== 'object') return;

          switch (data.type) {
            case 'ACTIVITY':
              lastActivityRef.current = Math.max(lastActivityRef.current, data.timestamp || Date.now());
              setShowWarning(false);
              break;
            case 'LOCK':
              setIsLocked(true);
              setShowWarning(false);
              lockedAtRef.current = data.lockedAt || Date.now();
              break;
            case 'UNLOCK':
              setIsLocked(false);
              setShowWarning(false);
              lockedAtRef.current = null;
              lastActivityRef.current = Date.now();
              break;
            case 'LOGOUT':
              setIsLocked(false);
              setShowWarning(false);
              if (signOut) signOut();
              break;
            default:
              break;
          }
        };
      }
    } catch (e) {
      console.warn('BroadcastChannel initialization error:', e);
    }

    // Storage event fallback for cross-tab communication
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_SYNC_KEY && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          if (data.type === 'ACTIVITY') {
            lastActivityRef.current = Math.max(lastActivityRef.current, data.timestamp || Date.now());
            setShowWarning(false);
          } else if (data.type === 'LOCK') {
            setIsLocked(true);
            setShowWarning(false);
            lockedAtRef.current = data.lockedAt || Date.now();
          } else if (data.type === 'UNLOCK') {
            setIsLocked(false);
            setShowWarning(false);
            lockedAtRef.current = null;
            lastActivityRef.current = Date.now();
          } else if (data.type === 'LOGOUT') {
            setIsLocked(false);
            setShowWarning(false);
            if (signOut) signOut();
          }
        } catch (err) {
          // Ignore parse errors
        }
      } else if (e.key === STORAGE_LOCK_KEY) {
        setIsLocked(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
      }
    };
  }, [signOut]);

  // Setup DOM interaction listeners to detect user activity
  useEffect(() => {
    if (!user || isLocked) return;

    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    const handleEvent = () => recordUserActivity();

    events.forEach((eventName) => {
      window.addEventListener(eventName, handleEvent, { passive: true });
    });

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handleEvent);
      });
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }
    };
  }, [user, isLocked, recordUserActivity]);

  // Keyboard shortcut: Alt + L to lock workstation immediately
  useEffect(() => {
    if (!user || isLocked) return;

    const handleKeyDown = (e) => {
      if (e.altKey && (e.key === 'l' || e.key === 'L')) {
        e.preventDefault();
        lockStation('manual');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user, isLocked, lockStation]);

  // Periodic heartbeat timer (checks every 1 second)
  useEffect(() => {
    // Wait until Firebase Auth finishes rehydrating session on page refresh before evaluating
    if (authLoading) return;

    if (!user) {
      setIsLocked(false);
      setShowWarning(false);
      try {
        localStorage.removeItem(STORAGE_LOCK_KEY);
        localStorage.removeItem(STORAGE_LOCKED_AT_KEY);
      } catch (e) {
        // Ignore
      }
      return;
    }

    const timer = setInterval(() => {
      const now = Date.now();

      // CASE A: Workstation is currently locked
      if (isLocked) {
        setShowWarning(false);
        const lockedAt = lockedAtRef.current || now;
        const timeLocked = now - lockedAt;

        // If locked for longer than lockTimeoutMs (60 minutes), hard sign-out
        if (timeLocked >= lockTimeoutMs) {
          handleHardLogout('lock_timeout');
        }
        return;
      }

      // CASE B: Workstation is active, check idle timeout
      const idleElapsed = now - lastActivityRef.current;
      const warningThreshold = idleTimeoutMs - warningDurationMs;

      if (idleElapsed >= idleTimeoutMs) {
        // Idle limit exceeded -> Engage Screen Lock
        lockStation('idle');
      } else if (idleElapsed >= warningThreshold) {
        // In warning zone -> show warning modal with countdown
        const remainingMs = idleTimeoutMs - idleElapsed;
        const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
        setShowWarning(true);
        setCountdownSeconds(remainingSec);
      } else {
        setShowWarning(false);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [user, authLoading, isLocked, idleTimeoutMs, warningDurationMs, lockTimeoutMs, lockStation, handleHardLogout]);

  const value = {
    isLocked,
    showWarning,
    countdownSeconds,
    lockStation,
    unlockStation,
    extendSession,
    handleHardLogout,
    sessionConfig: config
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;
